import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Table, Spinner, Alert, Button } from 'react-bootstrap';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { useAuth } from '../../context/AuthContext';
import colors from '../../constants/colors';
import { formatPrice } from '../../utils/formatPrice';
// import { Buffer } from 'buffer'; // Descomentar se Buffer não estiver globalmente disponível

// Detalhes de encomenda exigem posse: o backend aceita o dono autenticado OU
// o `X-Order-Token` — o token vem do fluxo de pagamento via location.state.
const getOrderHeaders = (state) => {
  const token = state?.orderDetails?.access_token;
  return token ? { 'X-Order-Token': token } : {};
};

/**
 * A API cancela enquanto a encomenda não estiver paga; uma paga responde 409 e
 * o cancelamento passa pela loja (reembolso). `processing` significa paga, por
 * isso não entra aqui — mostrar lá o botão era oferecer uma ação que nunca
 * podia resultar, e escondê-lo em `awaiting_payment` tirava-o precisamente do
 * estado em que a encomenda ainda se cancela sozinha.
 */
const CANCELABLE_STATUSES = ['pending', 'awaiting_payment'];

const MyOrderDetailsPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Falhar a cancelar não é falhar a carregar: o `error` acima troca a página
  // toda por um ecrã de erro, e a encomenda tem de continuar visível para quem
  // acabou de ler que o cancelamento passa pela loja.
  const [cancelError, setCancelError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Redirect if not logged in
    if (!user) {
      navigate('/login', { state: { from: `/minhas-encomendas/${id}` } });
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        const response = await axiosInstance.get(`/order/myorders/${id}`, {
          headers: getOrderHeaders(location.state),
        });
        setOrder(response);
      } catch (err) {
        console.error('Erro ao carregar detalhes da encomenda:', err);
        setError('Não foi possível carregar os detalhes da encomenda. Por favor tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id, navigate, user, location.state]);

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'Pendente', variant: 'warning' },
      'awaiting_payment': { text: 'A Aguardar Pagamento', variant: 'warning' },
      'processing': { text: 'Em Processamento', variant: 'info' },
      'shipped': { text: 'Enviada', variant: 'primary' },
      'delivered': { text: 'Entregue', variant: 'success' },
      'canceled': { text: 'Cancelada', variant: 'danger' },
      'payment_failed': { text: 'Pagamento Falhado', variant: 'danger' },
      'payment_initiation_failed': { text: 'Pagamento Não Iniciado', variant: 'danger' }
    };
    
    const defaultStatus = { text: status, variant: 'secondary' };
    return statusMap[status] || defaultStatus;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('pt-PT', options);
  };
  
  const handleCancelOrder = async () => {
    if (!window.confirm('Tem a certeza que deseja cancelar esta encomenda?')) {
      return;
    }
    
    try {
      setLoading(true);
      setCancelError(null);
      // O cancelamento é POST e a posse de convidado prova-se pelo token
      await axiosInstance.post(`/order/${id}/cancel`, {}, { headers: getOrderHeaders(location.state) });

      // Reload order details to show updated status
      const response = await axiosInstance.get(`/order/myorders/${id}`, {
        headers: getOrderHeaders(location.state),
      });
      setOrder(response);
      
    } catch (err) {
      // O 409 de encomenda paga é permanente e informativo — mostrar a mensagem
      // da API em vez de um "tente novamente" que nunca vai resultar.
      setCancelError(
        err.response?.data?.message ||
          'Não foi possível cancelar a encomenda. Por favor tente novamente mais tarde.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">A carregar...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate('/minhas-encomendas')} style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}>
          Voltar às Minhas Encomendas
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">Detalhes da Encomenda</h1>
      
      {order && (
        <>
          <Row className="mb-4">
            <Col md={6} lg={8}>
              <Card className="mb-4">
                <Card.Header as="h5">Informação da Encomenda</Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6}>
                      <p><strong>Encomenda Nº:</strong> #{order.id}</p>
                      <p><strong>Data:</strong> {formatDate(order.order_date)}</p>
                      <p>
                        <strong>Estado:</strong>{' '}
                        <Badge bg={getStatusBadge(order.status).variant}>
                          {getStatusBadge(order.status).text}
                        </Badge>
                      </p>
                    </Col>
                    <Col sm={6}>
                      <p><strong>Total:</strong> {formatPrice(order.total_price_with_tax)}€</p>
                      <p><strong>Método de Pagamento:</strong> {order.payment_method === 'multibanco' ? 'Multibanco' : order.payment_method === 'mbway' ? 'MB WAY' : order.payment_method}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} lg={4}>
              <Card>
                <Card.Header as="h5">Informação de Envio</Card.Header>
                <Card.Body>
                  <p><strong>Morada:</strong> {order.shipping_address}</p>
                  <p><strong>Telefone:</strong> {order.phone}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-4">
            <Card.Header as="h5">Itens da Encomenda</Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Preço</th>
                      <th>Quantidade</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.orderItems?.map((item) => {
                      const productInfo = item.product; 
                      const variantInfo = item.variant;

                      let displayProductName = `Produto #${item.product_id}`;
                      // Nome do produto: se for variante, usa "Nome Produto Pai - Nome Variante", senão "Nome Produto"
                      if (variantInfo && variantInfo.product) {
                        displayProductName = `${variantInfo.product.name || 'Produto'} - ${variantInfo.name}`;
                      } else if (productInfo) {
                        displayProductName = productInfo.name;
                      }

                      let itemImageSrc = '/placeholder-image.jpg';
                      let imageInfo = null;

                      // Priorizar imagem do produto pai da variante, se for uma variante
                      if (variantInfo && variantInfo.product && variantInfo.product.images && variantInfo.product.images.length > 0) {
                        imageInfo = variantInfo.product.images[0];
                      } 
                      // Caso contrário, usar imagem do produto principal do item
                      else if (productInfo && productInfo.images && productInfo.images.length > 0) {
                        imageInfo = productInfo.images[0];
                      }

                      if (imageInfo && imageInfo.src) {
                        itemImageSrc = imageInfo.src;
                      } else if (imageInfo && imageInfo.data_detail && imageInfo.mimetype) { // Fallback caso src não venha mas data_detail sim
                        let base64Data = imageInfo.data_detail;
                        if (typeof base64Data === 'object' && base64Data !== null && base64Data.type === 'Buffer' && Array.isArray(base64Data.data)) {
                          base64Data = Buffer.from(base64Data.data).toString('base64');
                        }
                        itemImageSrc = `data:${imageInfo.mimetype};base64,${base64Data}`;
                      }
                      
                      const viewProductUrlId = productInfo?.id;

                      return (
                        <tr key={item.id || item.product_id}> 
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={itemImageSrc}
                                alt={displayProductName}
                                style={{ width: '50px', height: '50px', objectFit: 'cover', marginRight: '10px' }}
                              />
                              <div>
                                <p className="mb-0">
                                  {displayProductName}
                                </p>
                                {viewProductUrlId && (
                                  <small>
                                    <Link to={`/produtos/${viewProductUrlId}`}>Ver produto</Link>
                                  </small>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{formatPrice(item.price)}€</td>
                          <td>{item.quantity}</td>
                          <td>{formatPrice(item.price * item.quantity)}€</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Soma de Produtos:</strong></td>
                      <td>{formatPrice(order.total_price_without_tax + order.total_tax)}€</td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Envio:</strong></td>
                      <td>{formatPrice(order.shipping_cost || 0)}€</td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="text-end"><strong>IVA:</strong></td>
                      <td>{formatPrice(order.total_tax)}€</td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Total a Pagar:</strong></td>
                      <td><strong>{formatPrice(order.total_price_with_tax)}€</strong></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {cancelError && (
            <Alert variant="danger" onClose={() => setCancelError(null)} dismissible>
              {cancelError}
            </Alert>
          )}

          <div className="d-flex justify-content-between">
            <Button variant="secondary" onClick={() => navigate('/minhas-encomendas')}>
              Voltar às Minhas Encomendas
            </Button>
            {CANCELABLE_STATUSES.includes(order.status) && (
              <Button 
                variant="danger" 
                onClick={handleCancelOrder}
                disabled={loading}
              >
                {loading ? 'A processar...' : 'Cancelar Encomenda'}
              </Button>
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export default MyOrderDetailsPage;
